// As this component is only used in a mapbox tooltip it cannot use emotion CSS. All styles need to be manually added to each component.

import { Box, Divider, Typography } from '@mui/material'
import { Icon } from '@app/components/Atoms/Icon'
import { sanitizeMarkdownToHtml } from '@app/components/Atoms/UiMarkdown/sanitizeMarkdownToHtml'
import { Solver } from '@app/utils/IconColourer'
import { firstLetterToUpperCase, generateS3Url } from '@app/utils/strings.utils'
import { formatAalBand, reasonMessage } from '@app/components/Atoms/AalStat/AalStat.format'
import type { AalReason } from '@app/hooks/useAal'

export interface MapTooltipProps {
  asset?: {
    id: number
    icon: Icon
    name: string
    type: string
    sentence: string
    identifyingInformation: { [key: string]: string | number } | null
    vulnerabilityLabel?: string
    vulnerabilityColour?: string
    exposure?: string
    exposureUnit?: string
  }
  hazards?: {
    title: string
    hazard_id?: string
    sentence: string
    vulnerabilityColour: string
    exposure_value: string
    metric: number
    unit: string
    vulnerabilityLabel: string
    /** Scenario parameters (e.g. slr/year/intervention) for the current hazard scenario. */
    parameters?: { [key: string]: string | number | number[] }
    aal_min?: number | null
    aal_max?: number | null
    aal_reason?: AalReason
  }[]
  information?: {
    id: number
    title: string
    icon?: Icon
    value?: string
  }
  mainColour: string
  prefer_hazard_term: boolean
  prefer_risk_term: boolean
  knownRiskIcons: string[]
}

/**
 * A tooltip component for displaying information about a map feature
 * Returns null if there is no asset sentence, no information value, and no hazards
 */
export const RiskMapTooltip = ({
  asset,
  hazards,
  information,
  mainColour: primaryColour,
  prefer_hazard_term,
  prefer_risk_term,
  knownRiskIcons,
}: MapTooltipProps) => {
  // React hooks and emotion CSS are not allowed in this file - MAPBOX

  const iconColourSolver = new Solver(primaryColour)
  const iconColourSolution = iconColourSolver.solve()

  const has_hazards = hazards && hazards.length > 0 && hazards[0] !== undefined
  const has_known_risks = knownRiskIcons.length > 0
  const has_element = !!(asset || information)

  return has_element ? (
    <Box
      className="tooltip-container"
      style={{
        padding: '4px 0px 4px 0px',
        backgroundColor: 'white',
        borderRadius: '4px',
        position: 'relative',
        boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
      }}
    >
      {/* 1. Element header — always at top */}
      {asset && (
        <>
          <Box
            component="span"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '6px',
              height: '100%',
              backgroundColor: asset.vulnerabilityColour,
              borderRadius: '4px 0 0 4px',
            }}
          />
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Box
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '8px' }}
            >
              {asset.icon.includes('s3://') ? (
                <img
                  src={generateS3Url(asset.icon)}
                  style={{ width: '24px', height: '24px', filter: iconColourSolution.filter }}
                />
              ) : (
                asset.icon && (
                  <Icon iconName={firstLetterToUpperCase(asset.icon) as Icon} size="large" />
                )
              )}
              <Typography id="tooltip-title">{asset.name}</Typography>
            </Box>
            <Box
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '4px' }}
            >
              <Typography>ID</Typography>
              <Typography>{asset.id}</Typography>
            </Box>
          </Box>
        </>
      )}

      {information && (
        <>
          <Box
            component="span"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '6px',
              height: '100%',
              borderRadius: '4px 0 0 4px',
            }}
          />
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Box
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '8px' }}
            >
              {information.icon && (
                <Icon iconName={firstLetterToUpperCase(information.icon) as Icon} size="large" />
              )}
              <Typography id="tooltip-title">{information.title}</Typography>
            </Box>
            <Box
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '4px' }}
            >
              <Typography>ID</Typography>
              <Typography>{information.id}</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* 2. Risk Sources */}
      {has_hazards && (
        <>
          <Divider light />
          <Box style={{ padding: '8px 16px' }}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: '8px',
                padding: '4px 0',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {prefer_hazard_term ? 'Hazards' : 'Risk Sources'}
              </Typography>
            </Box>
            <Box
              id="hazard-container"
              style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '4px 0' }}
            >
              {hazards.map(
                (hazard, index) =>
                  hazard && (
                    <Box
                      key={hazard.title + index}
                      style={{
                        border: '1px solid #e7e7e7',
                        borderRadius: '4px',
                        padding: '8px 8px 12px 18px',
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="span"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '6px',
                          height: '100%',
                          backgroundColor: hazard.vulnerabilityColour,
                          borderRadius: '4px 0 0 4px',
                        }}
                      />
                      <Typography variant="subtitle2">{hazard.title}</Typography>
                      <div
                        id="hazard-sentence"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeMarkdownToHtml(hazard.sentence),
                        }}
                      />
                      <Divider light />
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start',
                          marginTop: '4px',
                          gap: '16px',
                        }}
                      >
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'start',
                            gap: '4px',
                          }}
                        >
                          <Typography>{prefer_risk_term ? 'Risk' : 'Consequence'}:</Typography>
                          <Typography id="single-vulnerability-value">
                            {hazard.vulnerabilityLabel}
                          </Typography>
                        </Box>
                        {hazard.exposure_value !== null && (
                          <Box
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'start',
                              gap: '4px',
                            }}
                          >
                            <Typography>Exposure:</Typography>
                            <Typography id="single-exposure-value">
                              {hazard.exposure_value} {hazard.unit ?? ''}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {(hazard.aal_min != null && hazard.aal_max != null) || hazard.aal_reason ? (
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'start',
                            gap: '4px',
                            marginTop: '4px',
                          }}
                          title={reasonMessage(hazard.aal_reason)}
                        >
                          <Typography style={{ fontWeight: 600 }}>Event Set AAL:</Typography>
                          <Typography id="single-aal-value">
                            {hazard.aal_min != null && hazard.aal_max != null
                              ? `${formatAalBand(hazard.aal_min, hazard.aal_max, '$')} / yr`
                              : 'N/A'}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ),
              )}
            </Box>
          </Box>
        </>
      )}

      {/* 3. Known risks */}
      {has_known_risks && (
        <>
          <Divider />
          <Box style={{ padding: '8px 16px' }}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: '8px',
                padding: '4px 0',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Other known {prefer_hazard_term ? 'hazards' : 'sources of risk'}
              </Typography>
            </Box>
            <Box
              id="known-risks-icon-container"
              style={{ display: 'flex', flexDirection: 'row', gap: '8px', margin: '4px 0' }}
            >
              {knownRiskIcons.map((knownRiskIcon) => (
                <Icon key={knownRiskIcon} iconName={knownRiskIcon} size="medium" />
              ))}{' '}
            </Box>
          </Box>
        </>
      )}

      {/* 4. Element details */}
      {asset && (
        <>
          <Divider light />
          {asset.sentence ? (
            <Box style={{ padding: '8px 16px 8px 16px' }}>
              <div
                id="asset-sentence"
                dangerouslySetInnerHTML={{ __html: sanitizeMarkdownToHtml(asset.sentence) }}
              />
            </Box>
          ) : (
            <Box style={{ padding: '8px 16px 8px 16px', fontStyle: 'italic' }}>
              <Typography>Additional information not available.</Typography>
            </Box>
          )}
        </>
      )}

      {information && (
        <>
          <Divider light />
          {information.value ? (
            <Box id="information-container" style={{ padding: '12px 16px 12px 16px' }}>
              <div
                dangerouslySetInnerHTML={{ __html: sanitizeMarkdownToHtml(information.value) }}
              />
            </Box>
          ) : (
            <Box style={{ padding: '12px 16px 0 16px', fontStyle: 'italic' }}>
              <Typography>Additional information not available.</Typography>
            </Box>
          )}
        </>
      )}

      {/* 5. CTA */}
      <Divider light />
      <Box style={{ padding: '8px 16px' }}>
        <Typography style={{ fontStyle: 'italic' }}>Select element for more details.</Typography>
      </Box>
    </Box>
  ) : null
}
